// ============================================================================
// USE WORK ITEM HIERARCHY HOOK
// Manages hierarchical work item operations and tree state
// Date: October 25, 2025
// ============================================================================

'use client';

import { useState, useCallback, useEffect } from 'react';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkItem {
  id: string;
  item_number: string;
  title: string;
  description?: string;
  item_type: string;
  item_subtype?: string;
  status: string;
  priority?: string;
  assigned_to?: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  progress_percentage?: number;
  parent_id?: string;
  children?: WorkItem[];
  created_at?: string;
  updated_at?: string;
}

export interface BreadcrumbItem {
  id: string;
  item_number: string;
  title: string;
  item_type: string;
  item_subtype?: string;
}

export interface HierarchyRule {
  id: string;
  parent_type: string;
  parent_subtype?: string | null;
  child_type: string;
  child_subtype?: string | null;
  enabled: boolean;
  cascade_status?: boolean;
  cascade_delete?: boolean;
  rollup_progress?: boolean;
  rollup_time?: boolean;
  rollup_cost?: boolean;
  inherit_assignee?: boolean;
  inherit_tags?: boolean;
  min_depth?: number;
  max_depth?: number;
  required_fields?: string[];
}

// ============================================================================
// HOOK
// ============================================================================

export function useWorkItemHierarchy() {
  const [hierarchyRules, setHierarchyRules] = useState<HierarchyRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);

  // Load hierarchy rules for a client
  const loadHierarchyRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch('/api/v1/work-items/hierarchy-rules', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load hierarchy rules');
      }

      const data = await response.json();
      if (data.success) {
        setHierarchyRules(data.data.rules || []);
      }
    } catch (error) {
      console.error('[useWorkItemHierarchy] Error loading rules:', error);
    } finally {
      setLoadingRules(false);
    }
  }, []);

  // Get breadcrumb path for a work item
  const getItemPath = useCallback(async (itemId: string): Promise<BreadcrumbItem[]> => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`/api/v1/work-items/${itemId}/path`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load item path');
      }

      const data = await response.json();
      if (data.success) {
        return data.data.path || [];
      }
      return [];
    } catch (error) {
      console.error('[useWorkItemHierarchy] Error loading path:', error);
      return [];
    }
  }, []);

  // Validate if a parent-child relationship is allowed
  const validateHierarchy = useCallback(
    (parentType: string, childType: string): boolean => {
      const rule = hierarchyRules.find(
        r => r.parent_type === parentType && r.child_type === childType && r.enabled
      );
      return rule ? true : false;
    },
    [hierarchyRules]
  );

  // Get allowed child types for a parent
  const getAllowedChildTypes = useCallback(
    (parentType: string): string[] => {
      return hierarchyRules
        .filter(r => r.parent_type === parentType && r.enabled)
        .map(r => r.child_type);
    },
    [hierarchyRules]
  );

  // Build tree structure from flat list
  const buildTree = useCallback((items: WorkItem[]): WorkItem[] => {
    // Create a map for quick lookup
    const itemMap = new Map<string, WorkItem & { children: WorkItem[] }>();
    const rootItems: (WorkItem & { children: WorkItem[] })[] = [];

    // First pass: create all items with empty children arrays
    items.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    // Second pass: build parent-child relationships
    items.forEach(item => {
      const itemWithChildren = itemMap.get(item.id)!;
      if (item.parent_id) {
        const parent = itemMap.get(item.parent_id);
        if (parent) {
          parent.children.push(itemWithChildren);
        } else {
          // Parent not in list, treat as root
          rootItems.push(itemWithChildren);
        }
      } else {
        // No parent, it's a root item
        rootItems.push(itemWithChildren);
      }
    });

    return rootItems;
  }, []);

  // Find item in tree by ID
  const findItemInTree = useCallback(
    (tree: WorkItem[], itemId: string): WorkItem | null => {
      for (const item of tree) {
        if (item.id === itemId) return item;
        if (item.children && item.children.length > 0) {
          const found = findItemInTree(item.children, itemId);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  // Get all descendants of an item
  const getDescendants = useCallback((item: WorkItem): WorkItem[] => {
    const descendants: WorkItem[] = [];
    
    const collectDescendants = (current: WorkItem) => {
      if (current.children) {
        current.children.forEach(child => {
          descendants.push(child);
          collectDescendants(child);
        });
      }
    };
    
    collectDescendants(item);
    return descendants;
  }, []);

  // Calculate tree depth
  const getTreeDepth = useCallback((item: WorkItem): number => {
    if (!item.children || item.children.length === 0) {
      return 1;
    }
    return 1 + Math.max(...item.children.map(child => getTreeDepth(child)));
  }, []);

  // Load rules on mount
  useEffect(() => {
    loadHierarchyRules();
  }, [loadHierarchyRules]);

  return {
    hierarchyRules,
    loadingRules,
    loadHierarchyRules,
    getItemPath,
    validateHierarchy,
    getAllowedChildTypes,
    buildTree,
    findItemInTree,
    getDescendants,
    getTreeDepth,
  };
}

export default useWorkItemHierarchy;
